/**
 * Finding the YouTube video a note points at.
 *
 * The server does this too and is the authority — this exists only so the
 * Summarize button can enable itself without a round trip. Keep the two in
 * step: core/utils/video_summary.py.
 */

const YOUTUBE_HOSTS = new Set([
  'youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com',
  'youtube-nocookie.com', 'www.youtube-nocookie.com',
  'youtu.be', 'www.youtu.be',
]);

// /shorts/, /live/, /embed/ and /v/ carry the id in the path; /watch uses ?v=
const PATH_ID_PREFIXES = ['/shorts/', '/live/', '/embed/', '/v/'];

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

const URL_IN_TEXT = /https?:\/\/[^\s<>"')\]]+/g;

export interface NoteLink {
  url: string;
  link_type: string;
}

/** The 11-character video id, or null if this is not a single YouTube video. */
export const youtubeVideoId = (url?: string | null): string | null => {
  if (!url) return null;

  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (!YOUTUBE_HOSTS.has(parsed.hostname.toLowerCase())) return null;

  const path = parsed.pathname || '';

  if (parsed.hostname.toLowerCase().endsWith('youtu.be')) {
    const candidate = path.replace(/^\//, '').split('/')[0];
    return VIDEO_ID.test(candidate) ? candidate : null;
  }

  if (path === '/watch' || path === '/watch/') {
    const candidate = parsed.searchParams.get('v') || '';
    return VIDEO_ID.test(candidate) ? candidate : null;
  }

  for (const prefix of PATH_ID_PREFIXES) {
    if (path.startsWith(prefix)) {
      const candidate = path.slice(prefix.length).split('/')[0];
      return VIDEO_ID.test(candidate) ? candidate : null;
    }
  }

  // Playlists, channels and search results have no single video to summarize
  return null;
};

export const isYoutubeUrl = (url?: string | null): boolean => youtubeVideoId(url) !== null;

/** The plain watch URL, without playlist or timestamp params. */
export const canonicalYoutubeUrl = (url?: string | null): string | null => {
  const id = youtubeVideoId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
};

/**
 * The first YouTube video among a note's links, else in its text.
 * Links win because the user attached them deliberately.
 */
export const findYoutubeUrl = (
  links: NoteLink[] = [],
  content: string = ''
): string | null => {
  for (const link of links) {
    const canonical = canonicalYoutubeUrl(link?.url);
    if (canonical) return canonical;
  }

  for (const match of content.match(URL_IN_TEXT) || []) {
    const canonical = canonicalYoutubeUrl(match.replace(/[.,;:!?]+$/, ''));
    if (canonical) return canonical;
  }

  return null;
};
